import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { budgetsApi, financialReviewApi, reviewRequestsApi } from '../services/api';
import api from '../services/api';
import { refreshNotificationCounts } from '../lib/notificationUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Calendar,
  Lock,
  Unlock,
  ChevronDown,
  ChevronRight,
  FileText,
  Clock,
  Building2,
  RefreshCw,
  Layers,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Send,
  AlertTriangle,
  Calculator
} from 'lucide-react';

const MONTHS = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];
const MONTH_KEYS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];

const CATEGORY_COLORS = {
  'Sales': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  'Purchases': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  'Overhead': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  'Salaries': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  'Management Fees': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  'Other Expenses': { bg: 'bg-slate-50 dark:bg-zinc-800/50', text: 'text-slate-700 dark:text-zinc-400', border: 'border-slate-200 dark:border-zinc-700' },
};

const DISAGREEMENT_REASONS = [
  { value: 'timing', label: 'Timing Issue' },
  { value: 'allocation', label: 'Incorrect Allocation' },
  { value: 'unexpected', label: 'Unexpected Expense' },
  { value: 'missing', label: 'Missing Data' },
  { value: 'other', label: 'Other' },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return value < 0 ? `(${formatted})` : formatted;
};

const FinancialReviewPage = () => {
  const navigate = useNavigate();
  const { isDark, setSelectedBu: setThemeBu } = useTheme();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBuFilterLocal, setSelectedBuFilterLocal] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [budgetData, setBudgetData] = useState(null);
  const [reviewStarted, setReviewStarted] = useState(false);
  const [reviewLocked, setReviewLocked] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [disagreementDialog, setDisagreementDialog] = useState({ open: false, item: null });
  const [submittingReviewRequest, setSubmittingReviewRequest] = useState(false);
  
  // Wrapper to update both local state and theme context for BU logo
  const setSelectedBuFilter = (buId) => {
    setSelectedBuFilterLocal(buId);
    const bu = businessUnits.find(b => b.id === buId);
    if (setThemeBu) {
      setThemeBu(buId || '', bu?.logo_url);
    }
  };
  
  // For backward compatibility
  const selectedBuFilter = selectedBuFilterLocal;
  
  // Disagreement form state
  const [disagreementForm, setDisagreementForm] = useState({
    reason: '',
    comment: '',
    suggested_action: ''
  });
  
  // Collapsible state for categories and main accounts (matching Budget template)
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses']));
  const [expandedMainAccounts, setExpandedMainAccounts] = useState(new Set());
  
  // Review status tracking per line item
  const [reviewStatus, setReviewStatus] = useState({});
  
  // User permissions - non-admin users can only see their assigned BU
  const userBuId = user?.business_unit_id;
  const canSeeAllBUs = isAdmin() || user?.is_cfo || !userBuId;

  useEffect(() => {
    loadData();
  }, []);
  
  // Set default BU filter for restricted users
  useEffect(() => {
    if (userBuId && !canSeeAllBUs && !selectedBuFilter) {
      setSelectedBuFilter(userBuId);
    }
  }, [userBuId, canSeeAllBUs]);

  useEffect(() => {
    if (selectedBuFilter) {
      loadBudgets(selectedBuFilter);
    } else {
      loadBudgets();
    }
  }, [selectedBuFilter]);

  const loadData = async () => {
    try {
      const [budgetsRes, busRes] = await Promise.all([
        budgetsApi.getAll(),
        api.get('/settings/business-units')
      ]);
      setBudgets(budgetsRes.data);
      setBusinessUnits(busRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadBudgets = async (buId = null) => {
    try {
      const params = {};
      if (buId) params.business_unit_id = buId;
      const res = await budgetsApi.getAll(params);
      setBudgets(res.data);
    } catch (error) {
      toast.error('Failed to load budgets');
    }
  };

  const startReview = async () => {
    if (!selectedBudget || !selectedMonth) {
      toast.error('Please select a budget and month');
      return;
    }
    
    setLoadingReview(true);
    try {
      const res = await budgetsApi.getOne(selectedBudget);
      setBudgetData(res.data);
      setReviewStarted(true);
      setReviewStatus({});
    } catch (error) {
      toast.error('Failed to load budget data');
    } finally {
      setLoadingReview(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      newSet.has(category) ? newSet.delete(category) : newSet.add(category);
      return newSet;
    });
  };

  const toggleMainAccount = (accountCode) => {
    setExpandedMainAccounts(prev => {
      const newSet = new Set(prev);
      newSet.has(accountCode) ? newSet.delete(accountCode) : newSet.add(accountCode);
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses']));
    const allMainAccounts = budgetData?.line_items?.filter(i => i.is_header).map(i => i.account_code) || [];
    setExpandedMainAccounts(new Set(allMainAccounts));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedMainAccounts(new Set());
  };

  const handleAgree = (itemId) => {
    if (reviewLocked) return;
    setReviewStatus(prev => ({
      ...prev,
      [itemId]: { status: 'agreed', reviewed_by: user?.name, reviewed_at: new Date().toISOString() }
    }));
    toast.success('Marked as agreed');
  };

  const openDisagreementDialog = (item) => {
    if (reviewLocked) return;
    // Calculate budget and actual for the item
    const budget = calculateItemBudget(item);
    const actual = calculateItemActual(item);
    const variance = actual - budget;
    
    setDisagreementForm({
      reason: '',
      comment: '',
      suggested_action: ''
    });
    setDisagreementDialog({ 
      open: true, 
      item,
      budget,
      actual,
      variance
    });
  };

  const handleDisagree = async () => {
    const item = disagreementDialog.item;
    
    if (!disagreementForm.reason) {
      toast.error('Please select a reason for disagreement');
      return;
    }
    
    if (!disagreementForm.comment) {
      toast.error('Please provide a detailed explanation');
      return;
    }
    
    try {
      setSubmittingReviewRequest(true);
      
      // Create review request to be sent to CFO
      await reviewRequestsApi.create({
        budget_id: selectedBudget,
        account_code: item.account_code,
        account_name: item.account_name,
        month: selectedMonthKey,
        reason: DISAGREEMENT_REASONS.find(r => r.value === disagreementForm.reason)?.label || disagreementForm.reason,
        comment: disagreementForm.comment,
        suggested_action: disagreementForm.suggested_action,
        budget_amount: disagreementDialog.budget || 0,
        actual_amount: disagreementDialog.actual || 0,
        variance: disagreementDialog.variance || 0
      });
      
      // Update local review status
      setReviewStatus(prev => ({
        ...prev,
        [item.account_code]: { 
          status: 'disagreed', 
          reason: disagreementForm.reason, 
          comment: disagreementForm.comment,
          suggested_action: disagreementForm.suggested_action,
          reviewed_by: user?.name, 
          reviewed_at: new Date().toISOString(),
          sent_to_cfo: true
        }
      }));
      
      setDisagreementDialog({ open: false, item: null });
      setDisagreementForm({ reason: '', comment: '', suggested_action: '' });
      toast.success('Disagreement submitted and sent to CFO for review');
      
      // Trigger refresh of notification counts in Layout
      refreshNotificationCounts();
    } catch (error) {
      console.error('Error submitting review request:', error);
      toast.error('Failed to submit review request');
    } finally {
      setSubmittingReviewRequest(false);
    }
  };

  const lockReview = () => {
    setReviewLocked(true);
    toast.success('Review locked. Changes can no longer be made.');
  };

  const unlockReview = () => {
    setReviewLocked(false);
    toast.info('Review unlocked for editing');
  };

  const resetReview = () => {
    setReviewStarted(false);
    setBudgetData(null);
    setReviewLocked(false);
    setReviewStatus({});
    setSelectedBudget('');
    setSelectedMonth('');
  };

  // Get month index for calculations
  const selectedMonthIndex = MONTHS.indexOf(selectedMonth);
  const selectedMonthKey = selectedMonthIndex >= 0 ? MONTH_KEYS[selectedMonthIndex] : null;

  // Group line items by category, then by main account (same as Budget template)
  const groupedItems = useMemo(() => {
    if (!budgetData?.line_items) return {};
    
    const groups = {};
    const categoryOrder = ['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses'];
    
    categoryOrder.forEach(cat => {
      groups[cat] = { mainAccounts: {} };
    });
    
    budgetData.line_items.forEach((item, index) => {
      const category = item.category;
      if (!category || !groups[category]) return;
      
      const itemWithIndex = { ...item, _index: index };
      
      if (item.is_header) {
        if (!groups[category].mainAccounts[item.account_code]) {
          groups[category].mainAccounts[item.account_code] = { header: itemWithIndex, subAccounts: [] };
        } else {
          groups[category].mainAccounts[item.account_code].header = itemWithIndex;
        }
      } else {
        const parentCode = item.parent_code || 'ungrouped';
        if (!groups[category].mainAccounts[parentCode]) {
          groups[category].mainAccounts[parentCode] = { header: null, subAccounts: [] };
        }
        groups[category].mainAccounts[parentCode].subAccounts.push(itemWithIndex);
      }
    });
    
    return groups;
  }, [budgetData?.line_items]);

  // Calculate totals for review
  const calculateItemBudget = (item) => {
    if (!selectedMonthKey) return 0;
    return item[selectedMonthKey] || 0;
  };

  const calculateItemActual = (item) => {
    if (!selectedMonthKey) return 0;
    // Return the actual value from the data (0 if not present)
    const actualValue = item[`${selectedMonthKey}_actual`];
    return actualValue !== undefined && actualValue !== null ? actualValue : 0;
  };

  // Calculate Full Year Budget - TOTAL of all 12 months budget (not YTD)
  const calculateFullYearBudget = (item) => {
    return MONTH_KEYS.reduce((sum, m) => {
      const budgetValue = item[m];
      return sum + (budgetValue !== undefined && budgetValue !== null ? budgetValue : 0);
    }, 0);
  };

  // Calculate YTD Actual - cumulative actuals from start of year to selected month
  const calculateYTDActual = (item) => {
    if (selectedMonthIndex < 0) return 0;
    return MONTH_KEYS.slice(0, selectedMonthIndex + 1).reduce((sum, m) => {
      // Return the actual value from the data (0 if not present)
      const actualValue = item[`${m}_actual`];
      return sum + (actualValue !== undefined && actualValue !== null ? actualValue : 0);
    }, 0);
  };

  const calculateMainAccountTotal = (mainAccount, calcFn) => {
    return mainAccount.subAccounts.reduce((sum, item) => sum + calcFn(item), 0);
  };

  const calculateCategoryTotal = (category, calcFn) => {
    const mainAccounts = groupedItems[category]?.mainAccounts || {};
    let total = 0;
    Object.values(mainAccounts).forEach(ma => {
      ma.subAccounts.forEach(item => {
        total += calcFn(item);
      });
    });
    return total;
  };

  // Summary totals
  const totals = useMemo(() => {
    const sales = { 
      budget: calculateCategoryTotal('Sales', calculateItemBudget), 
      actual: calculateCategoryTotal('Sales', calculateItemActual),
      ytdBudget: calculateCategoryTotal('Sales', calculateFullYearBudget),
      ytdActual: calculateCategoryTotal('Sales', calculateYTDActual)
    };
    const purchases = { 
      budget: calculateCategoryTotal('Purchases', calculateItemBudget), 
      actual: calculateCategoryTotal('Purchases', calculateItemActual),
      ytdBudget: calculateCategoryTotal('Purchases', calculateFullYearBudget),
      ytdActual: calculateCategoryTotal('Purchases', calculateYTDActual)
    };
    const overhead = {
      budget: calculateCategoryTotal('Overhead', calculateItemBudget),
      actual: calculateCategoryTotal('Overhead', calculateItemActual),
      ytdBudget: calculateCategoryTotal('Overhead', calculateFullYearBudget),
      ytdActual: calculateCategoryTotal('Overhead', calculateYTDActual)
    };
    const salaries = {
      budget: calculateCategoryTotal('Salaries', calculateItemBudget),
      actual: calculateCategoryTotal('Salaries', calculateItemActual),
      ytdBudget: calculateCategoryTotal('Salaries', calculateFullYearBudget),
      ytdActual: calculateCategoryTotal('Salaries', calculateYTDActual)
    };
    const managementFees = {
      budget: calculateCategoryTotal('Management Fees', calculateItemBudget),
      actual: calculateCategoryTotal('Management Fees', calculateItemActual),
      ytdBudget: calculateCategoryTotal('Management Fees', calculateFullYearBudget),
      ytdActual: calculateCategoryTotal('Management Fees', calculateYTDActual)
    };
    const otherExpenses = {
      budget: calculateCategoryTotal('Other Expenses', calculateItemBudget),
      actual: calculateCategoryTotal('Other Expenses', calculateItemActual),
      ytdBudget: calculateCategoryTotal('Other Expenses', calculateFullYearBudget),
      ytdActual: calculateCategoryTotal('Other Expenses', calculateYTDActual)
    };
    
    const grossProfit = {
      budget: sales.budget - purchases.budget,
      actual: sales.actual - purchases.actual,
      ytdBudget: sales.ytdBudget - purchases.ytdBudget,
      ytdActual: sales.ytdActual - purchases.ytdActual
    };
    
    // OPEX totals (expenses are stored as negative values)
    const opex = {
      budget: overhead.budget + salaries.budget + managementFees.budget,
      actual: overhead.actual + salaries.actual + managementFees.actual,
      ytdBudget: overhead.ytdBudget + salaries.ytdBudget + managementFees.ytdBudget,
      ytdActual: overhead.ytdActual + salaries.ytdActual + managementFees.ytdActual
    };
    
    // Operating Profit = Gross Profit + OPEX (OPEX is already negative)
    const operatingProfit = {
      budget: grossProfit.budget + opex.budget,
      actual: grossProfit.actual + opex.actual,
      ytdBudget: grossProfit.ytdBudget + opex.ytdBudget,
      ytdActual: grossProfit.ytdActual + opex.ytdActual
    };
    
    // Net Profit = Operating Profit + Other Expenses (Other Expenses is negative)
    const netProfit = {
      budget: operatingProfit.budget + otherExpenses.budget,
      actual: operatingProfit.actual + otherExpenses.actual,
      ytdBudget: operatingProfit.ytdBudget + otherExpenses.ytdBudget,
      ytdActual: operatingProfit.ytdActual + otherExpenses.ytdActual
    };
    
    return { sales, purchases, overhead, salaries, managementFees, otherExpenses, grossProfit, opex, operatingProfit, netProfit };
  }, [groupedItems, selectedMonthKey]);

  // Review stats
  const totalSubAccounts = useMemo(() => {
    let count = 0;
    Object.values(groupedItems).forEach(cat => {
      Object.values(cat.mainAccounts).forEach(ma => {
        count += ma.subAccounts.length;
      });
    });
    return count;
  }, [groupedItems]);

  const reviewedCount = Object.keys(reviewStatus).length;
  const agreedCount = Object.values(reviewStatus).filter(r => r.status === 'agreed').length;
  const disagreedCount = Object.values(reviewStatus).filter(r => r.status === 'disagreed').length;
  const progressPercent = totalSubAccounts > 0 ? (reviewedCount / totalSubAccounts) * 100 : 0;

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="financial-review-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="financial-review-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          {reviewStarted && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={resetReview} 
              className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="back-to-selection-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <div>
            <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Financial Review
            </h1>
            <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
              {reviewStarted 
                ? `Reviewing ${budgetData?.name} for ${selectedMonth}` 
                : 'Monthly budget review with detailed account breakdown'}
            </p>
          </div>
        </div>
        {reviewStarted && (
          <Button variant="outline" onClick={resetReview} className="rounded-sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Start New Review
          </Button>
        )}
      </div>

      {/* Budget & Month Selection */}
      {!reviewStarted && (
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
            <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              Start Financial Review
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {budgets.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                <FileText className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                <p className="font-medium">No budgets available</p>
                <p className="text-sm mt-1">Create a budget first to start reviewing.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Filter by Business Unit</Label>
                    <Select 
                      value={selectedBuFilter || 'all'} 
                      onValueChange={(v) => setSelectedBuFilter(v === 'all' ? '' : v)}
                      disabled={!canSeeAllBUs}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="select-bu-filter">
                        <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="All Business Units" />
                      </SelectTrigger>
                      <SelectContent>
                        {canSeeAllBUs && <SelectItem value="all">All Business Units</SelectItem>}
                        {businessUnits
                          .filter(bu => canSeeAllBUs || bu.id === userBuId)
                          .map(bu => (
                            <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Select Budget</Label>
                    <Select value={selectedBudget} onValueChange={setSelectedBudget}>
                      <SelectTrigger className="rounded-sm" data-testid="select-budget">
                        <SelectValue placeholder="Choose a budget" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgets.map(budget => (
                          <SelectItem key={budget.id} value={budget.id}>
                            <div className="flex items-center gap-2">
                              <span>{budget.name}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                budget.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                budget.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                budget.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {budget.status}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Review Month</Label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="rounded-sm" data-testid="select-month">
                        <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="Choose month" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((month) => (
                          <SelectItem key={month} value={month}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className={`mt-4 p-4 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    <strong>Note:</strong> The review will show budget figures using the same template structure as the Budgets page. 
                    Review each account line and mark as agreed or disagreed.
                  </p>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={startReview}
                    disabled={loadingReview}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                    data-testid="start-review-btn"
                  >
                    {loadingReview ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ClipboardCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Start Review
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review Interface - Matching Budget Template */}
      {reviewStarted && budgetData && (
        <>
          {/* Review Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className="p-4">
                <p className={isDark ? 'text-zinc-400 text-sm' : 'text-slate-500 text-sm'}>Review Progress</p>
                <div className="mt-2">
                  <Progress value={progressPercent} className="h-2" />
                  <p className={`text-sm mt-1 ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                    {reviewedCount}/{totalSubAccounts} items
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className="p-4">
                <p className={isDark ? 'text-zinc-400 text-sm' : 'text-slate-500 text-sm'}>Agreed</p>
                <p className="font-mono text-2xl font-medium mt-1 text-emerald-600">{agreedCount}</p>
              </CardContent>
            </Card>
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className="p-4">
                <p className={isDark ? 'text-zinc-400 text-sm' : 'text-slate-500 text-sm'}>Disagreed</p>
                <p className="font-mono text-2xl font-medium mt-1 text-red-600">{disagreedCount}</p>
              </CardContent>
            </Card>
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className="p-4">
                <p className={isDark ? 'text-zinc-400 text-sm' : 'text-slate-500 text-sm'}>Pending</p>
                <p className={`font-mono text-2xl font-medium mt-1 ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                  {totalSubAccounts - reviewedCount}
                </p>
              </CardContent>
            </Card>
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className={isDark ? 'text-zinc-400 text-sm' : 'text-slate-500 text-sm'}>Status</p>
                  <p className={`font-medium mt-1 ${reviewLocked ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {reviewLocked ? 'Locked' : 'In Progress'}
                  </p>
                </div>
                {reviewLocked ? (
                  <Unlock className="w-5 h-5 text-slate-400 cursor-pointer hover:text-slate-600" onClick={unlockReview} strokeWidth={1.5} />
                ) : (
                  <Lock className="w-5 h-5 text-slate-400 cursor-pointer hover:text-slate-600" onClick={lockReview} strokeWidth={1.5} />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Review Context Bar */}
          <div className={`p-4 rounded-sm flex items-center justify-between gap-4 flex-wrap ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {budgetData.name}
                </span>
              </div>
              <Badge variant="outline">{budgetData.fiscal_year}</Badge>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <span className={isDark ? 'text-zinc-300' : 'text-slate-600'}>
                  {budgetData.business_unit_name || 'All BUs'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <span className={isDark ? 'text-zinc-300' : 'text-slate-600'}>
                  Reviewing: <strong>{selectedMonth}</strong>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={expandAll} className="rounded-sm text-xs">
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll} className="rounded-sm text-xs">
                Collapse All
              </Button>
            </div>
          </div>

          {/* Category Tables - Same structure as Budget template */}
          <div className="space-y-4">
            {Object.entries(groupedItems).map(([category, data]) => {
              const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Other Expenses'];
              const categoryBudget = calculateCategoryTotal(category, calculateItemBudget);
              const categoryActual = calculateCategoryTotal(category, calculateItemActual);
              const categoryVariance = categoryActual - categoryBudget;
              const isExpanded = expandedCategories.has(category);
              const mainAccounts = Object.entries(data.mainAccounts).filter(([_, ma]) => ma.subAccounts.length > 0 || ma.header);
              
              if (mainAccounts.length === 0) return null;
              
              return (
                <Card key={category} className={`border shadow-none rounded-sm overflow-hidden ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                  <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(category)}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className={`py-3 px-4 ${colors.bg} ${colors.border} border-b cursor-pointer hover:opacity-90`}>
                        <div className="flex items-center justify-between">
                          <CardTitle className={`font-display text-base font-medium flex items-center gap-2 ${colors.text}`}>
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <Layers className="w-4 h-4" />
                            {category}
                            <span className={`text-xs font-normal ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                              ({mainAccounts.length} accounts)
                            </span>
                          </CardTitle>
                          <div className="flex items-center gap-6 font-mono text-sm">
                            <div className="text-right">
                              <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{selectedMonth} Actual</span>
                              <p className={colors.text}>{formatCurrency(categoryActual)}</p>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{selectedMonth} Budget</span>
                              <p className={colors.text}>{formatCurrency(categoryBudget)}</p>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</span>
                              <p className={categoryVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                {categoryVariance >= 0 ? '+' : ''}{formatCurrency(categoryVariance)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <CardContent className="p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                              <th className="text-left py-2 px-4 font-medium w-16">Code</th>
                              <th className="text-left py-2 px-4 font-medium min-w-[200px]">Account</th>
                              <th className="text-right py-2 px-3 font-medium">{selectedMonth} Actual</th>
                              <th className="text-right py-2 px-3 font-medium">{selectedMonth} Budget</th>
                              <th className="text-right py-2 px-3 font-medium">Variance</th>
                              <th className="text-right py-2 px-3 font-medium">YTD Actual</th>
                              <th className="text-right py-2 px-3 font-medium">Total Budget</th>
                              <th className="text-right py-2 px-3 font-medium">Var to Budget</th>
                              <th className="text-center py-2 px-3 font-medium w-24">Status</th>
                              <th className="text-center py-2 px-3 font-medium w-28">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mainAccounts.map(([accountCode, mainAccount]) => {
                              const hasSubAccounts = mainAccount.subAccounts.length > 0;
                              const isMainExpanded = expandedMainAccounts.has(accountCode);
                              const mainBudget = calculateMainAccountTotal(mainAccount, calculateItemBudget);
                              const mainActual = calculateMainAccountTotal(mainAccount, calculateItemActual);
                              const mainVariance = mainActual - mainBudget;
                              
                              return (
                                <Collapsible key={accountCode} open={isMainExpanded} onOpenChange={() => toggleMainAccount(accountCode)} asChild>
                                  <>
                                    {/* Main Account Header Row */}
                                    {mainAccount.header && (
                                      <CollapsibleTrigger asChild>
                                        <tr className={`border-t cursor-pointer ${isDark ? 'border-zinc-700 bg-zinc-800/30 hover:bg-zinc-800/50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}>
                                          <td className={`py-2 px-4 font-mono text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                            <div className="flex items-center gap-1">
                                              {hasSubAccounts && (isMainExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)}
                                              {mainAccount.header.account_code}
                                            </div>
                                          </td>
                                          <td className={`py-2 px-4 font-medium ${colors.text}`}>
                                            {mainAccount.header.account_name}
                                          </td>
                                          <td className={`py-2 px-3 text-right font-mono font-medium ${colors.text}`}>
                                            {formatCurrency(mainActual)}
                                          </td>
                                          <td className={`py-2 px-3 text-right font-mono font-medium ${colors.text}`}>
                                            {formatCurrency(mainBudget)}
                                          </td>
                                          <td className={`py-2 px-3 text-right font-mono font-medium ${mainVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {mainVariance >= 0 ? '+' : ''}{formatCurrency(mainVariance)}
                                          </td>
                                          <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                                            {formatCurrency(calculateMainAccountTotal(mainAccount, calculateYTDActual))}
                                          </td>
                                          <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                            {formatCurrency(calculateMainAccountTotal(mainAccount, calculateFullYearBudget))}
                                          </td>
                                          <td className={`py-2 px-3 text-right font-mono ${(calculateMainAccountTotal(mainAccount, calculateYTDActual) - calculateMainAccountTotal(mainAccount, calculateFullYearBudget)) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {formatCurrency(calculateMainAccountTotal(mainAccount, calculateYTDActual) - calculateMainAccountTotal(mainAccount, calculateFullYearBudget))}
                                          </td>
                                          <td></td>
                                          <td></td>
                                        </tr>
                                      </CollapsibleTrigger>
                                    )}
                                    
                                    {/* Sub-Account Rows */}
                                    <CollapsibleContent asChild>
                                      <>
                                        {mainAccount.subAccounts.map((item, idx) => {
                                          const itemBudget = calculateItemBudget(item);
                                          const itemActual = calculateItemActual(item);
                                          const variance = itemActual - itemBudget;
                                          const ytdBudget = calculateFullYearBudget(item);
                                          const ytdActual = calculateYTDActual(item);
                                          const ytdVariance = ytdActual - ytdBudget;
                                          const itemStatus = reviewStatus[item.account_code];
                                          
                                          return (
                                            <tr 
                                              key={idx} 
                                              className={`border-t ${isDark ? 'border-zinc-800 hover:bg-zinc-800/30' : 'border-slate-100 hover:bg-slate-50'} ${
                                                itemStatus?.status === 'disagreed' ? isDark ? 'bg-red-900/10' : 'bg-red-50/50' : ''
                                              }`}
                                            >
                                              <td className={`py-2 px-4 pl-8 font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                                {item.account_code}
                                              </td>
                                              <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                                {item.account_name}
                                              </td>
                                              <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                                {itemActual !== 0 ? formatCurrency(itemActual) : '-'}
                                              </td>
                                              <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                                {itemBudget !== 0 ? formatCurrency(itemBudget) : '-'}
                                              </td>
                                              <td className={`py-2 px-3 text-right font-mono ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                <div className="flex items-center justify-end gap-1">
                                                  {variance >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                                  {variance !== 0 ? `${variance >= 0 ? '+' : ''}${formatCurrency(variance)}` : '-'}
                                                </div>
                                              </td>
                                              <td className={`py-2 px-3 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                                {ytdActual !== 0 ? formatCurrency(ytdActual) : '-'}
                                              </td>
                                              <td className={`py-2 px-3 text-right font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                                {ytdBudget !== 0 ? formatCurrency(ytdBudget) : '-'}
                                              </td>
                                              <td className={`py-2 px-3 text-right font-mono text-xs ${ytdVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {ytdVariance !== 0 ? `${ytdVariance >= 0 ? '+' : ''}${formatCurrency(ytdVariance)}` : '-'}
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                {itemStatus?.status === 'agreed' && (
                                                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                                    Agreed
                                                  </Badge>
                                                )}
                                                {itemStatus?.status === 'disagreed' && (
                                                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs">
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    Disagreed
                                                  </Badge>
                                                )}
                                                {!itemStatus && (
                                                  <Badge variant="outline" className={`text-xs ${isDark ? 'border-zinc-600 text-zinc-400' : ''}`}>
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    Pending
                                                  </Badge>
                                                )}
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                {!reviewLocked && (
                                                  <div className="flex items-center justify-center gap-1">
                                                    <Button
                                                      size="sm"
                                                      variant={itemStatus?.status === 'agreed' ? 'default' : 'outline'}
                                                      className={`rounded-sm h-7 w-7 p-0 ${
                                                        itemStatus?.status === 'agreed' 
                                                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                                          : ''
                                                      }`}
                                                      onClick={() => handleAgree(item.account_code)}
                                                    >
                                                      <CheckCircle2 className="w-3 h-3" />
                                                    </Button>
                                                    <Button
                                                      size="sm"
                                                      variant={itemStatus?.status === 'disagreed' ? 'default' : 'outline'}
                                                      className={`rounded-sm h-7 w-7 p-0 ${
                                                        itemStatus?.status === 'disagreed' 
                                                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                          : ''
                                                      }`}
                                                      onClick={() => openDisagreementDialog(item)}
                                                    >
                                                      <XCircle className="w-3 h-3" />
                                                    </Button>
                                                  </div>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </>
                                    </CollapsibleContent>
                                  </>
                                </Collapsible>
                              );
                            })}
                            
                            {/* Category Total Row */}
                            <tr className={`border-t-2 ${isDark ? 'border-zinc-600 bg-zinc-800' : 'border-slate-300 bg-slate-100'}`}>
                              <td colSpan={2} className={`py-3 px-4 font-bold ${colors.text}`}>
                                Total {category}
                              </td>
                              <td className={`py-3 px-3 text-right font-mono font-bold ${colors.text}`}>
                                {formatCurrency(categoryActual)}
                              </td>
                              <td className={`py-3 px-3 text-right font-mono font-bold ${colors.text}`}>
                                {formatCurrency(categoryBudget)}
                              </td>
                              <td className={`py-3 px-3 text-right font-mono font-bold ${categoryVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {categoryVariance >= 0 ? '+' : ''}{formatCurrency(categoryVariance)}
                              </td>
                              <td className={`py-3 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                                {formatCurrency(calculateCategoryTotal(category, calculateYTDActual))}
                              </td>
                              <td className={`py-3 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                {formatCurrency(calculateCategoryTotal(category, calculateFullYearBudget))}
                              </td>
                              <td className={`py-3 px-3 text-right font-mono font-medium ${(calculateCategoryTotal(category, calculateYTDActual) - calculateCategoryTotal(category, calculateFullYearBudget)) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatCurrency(calculateCategoryTotal(category, calculateYTDActual) - calculateCategoryTotal(category, calculateFullYearBudget))}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })}
          </div>

          {/* Financial Summary Card */}
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`} data-testid="financial-summary-card">
            <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-base font-medium flex items-center gap-2">
                <Calculator className="w-4 h-4 text-emerald-600" />
                Financial Summary - {selectedMonth}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                    <th className="text-left py-3 px-4 font-medium min-w-[200px]">Description</th>
                    <th className="text-right py-3 px-3 font-medium w-32">{selectedMonth} Actual</th>
                    <th className="text-right py-3 px-3 font-medium w-32">{selectedMonth} Budget</th>
                    <th className="text-right py-3 px-3 font-medium w-32">Variance</th>
                    <th className="text-right py-3 px-3 font-medium w-32">YTD Actual</th>
                    <th className="text-right py-3 px-3 font-medium w-32">Full Year Budget</th>
                    <th className="text-right py-3 px-3 font-medium w-32">Var to Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Sales */}
                  <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Total Sales</td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-600">{formatCurrency(totals.sales.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.sales.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.sales.actual - totals.sales.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.sales.actual - totals.sales.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.sales.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.sales.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.sales.ytdActual - totals.sales.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.sales.ytdActual - totals.sales.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Cost of Sales */}
                  <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Cost of Sales</td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(totals.purchases.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.purchases.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.purchases.actual - totals.purchases.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.purchases.actual - totals.purchases.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.purchases.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.purchases.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.purchases.ytdActual - totals.purchases.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.purchases.ytdActual - totals.purchases.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Gross Profit */}
                  <tr className={`border-t-2 ${isDark ? 'border-zinc-700 bg-emerald-900/10' : 'border-slate-200 bg-emerald-50'}`}>
                    <td className={`py-2 px-4 font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Gross Profit</td>
                    <td className={`py-2 px-3 text-right font-mono font-semibold ${totals.grossProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.grossProfit.actual)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.grossProfit.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${(totals.grossProfit.actual - totals.grossProfit.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.grossProfit.actual - totals.grossProfit.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${totals.grossProfit.ytdActual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.grossProfit.ytdActual)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.grossProfit.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${(totals.grossProfit.ytdActual - totals.grossProfit.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.grossProfit.ytdActual - totals.grossProfit.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Overhead */}
                  <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Overhead</td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(totals.overhead.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.overhead.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.overhead.actual - totals.overhead.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.overhead.actual - totals.overhead.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.overhead.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.overhead.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.overhead.ytdActual - totals.overhead.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.overhead.ytdActual - totals.overhead.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Salaries */}
                  <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Salaries</td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(totals.salaries.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.salaries.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.salaries.actual - totals.salaries.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.salaries.actual - totals.salaries.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.salaries.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.salaries.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.salaries.ytdActual - totals.salaries.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.salaries.ytdActual - totals.salaries.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Management Fees */}
                  <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Management Fees</td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(totals.managementFees.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.managementFees.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.managementFees.actual - totals.managementFees.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.managementFees.actual - totals.managementFees.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.managementFees.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.managementFees.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.managementFees.ytdActual - totals.managementFees.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.managementFees.ytdActual - totals.managementFees.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Total OPEX */}
                  <tr className={`border-t ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-slate-200 bg-slate-50'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-200' : 'text-slate-800'}`}>Total Operating Expenses</td>
                    <td className="py-2 px-3 text-right font-mono font-medium text-red-600">{formatCurrency(totals.opex.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.opex.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${(totals.opex.actual - totals.opex.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.opex.actual - totals.opex.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.opex.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.opex.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${(totals.opex.ytdActual - totals.opex.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.opex.ytdActual - totals.opex.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Operating Profit */}
                  <tr className={`border-t ${isDark ? 'border-zinc-700 bg-blue-900/10' : 'border-slate-200 bg-blue-50'}`}>
                    <td className={`py-2 px-4 font-semibold ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>Operating Profit</td>
                    <td className={`py-2 px-3 text-right font-mono font-semibold ${totals.operatingProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.operatingProfit.actual)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.operatingProfit.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${(totals.operatingProfit.actual - totals.operatingProfit.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.operatingProfit.actual - totals.operatingProfit.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${totals.operatingProfit.ytdActual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.operatingProfit.ytdActual)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.operatingProfit.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${(totals.operatingProfit.ytdActual - totals.operatingProfit.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.operatingProfit.ytdActual - totals.operatingProfit.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Other Expenses */}
                  <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Other Expenses</td>
                    <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(totals.otherExpenses.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.otherExpenses.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.otherExpenses.actual - totals.otherExpenses.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.otherExpenses.actual - totals.otherExpenses.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(totals.otherExpenses.ytdActual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.otherExpenses.ytdBudget)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${(totals.otherExpenses.ytdActual - totals.otherExpenses.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.otherExpenses.ytdActual - totals.otherExpenses.ytdBudget)}
                    </td>
                  </tr>
                  
                  {/* Net Profit/(Loss) */}
                  <tr className={`border-t-2 ${isDark ? 'border-zinc-600 bg-zinc-800' : 'border-slate-300 bg-slate-100'}`}>
                    <td className={`py-3 px-4 font-bold text-lg ${totals.netProfit.actual >= 0 ? isDark ? 'text-emerald-400' : 'text-emerald-700' : isDark ? 'text-red-400' : 'text-red-700'}`}>
                      Net Profit / (Loss)
                    </td>
                    <td className={`py-3 px-3 text-right font-mono font-bold text-lg ${totals.netProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.netProfit.actual)}
                    </td>
                    <td className={`py-3 px-3 text-right font-mono font-bold ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.netProfit.budget)}</td>
                    <td className={`py-3 px-3 text-right font-mono font-bold ${(totals.netProfit.actual - totals.netProfit.budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.netProfit.actual - totals.netProfit.budget)}
                    </td>
                    <td className={`py-3 px-3 text-right font-mono font-bold ${totals.netProfit.ytdActual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.netProfit.ytdActual)}
                    </td>
                    <td className={`py-3 px-3 text-right font-mono font-bold ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.netProfit.ytdBudget)}</td>
                    <td className={`py-3 px-3 text-right font-mono font-bold ${(totals.netProfit.ytdActual - totals.netProfit.ytdBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(totals.netProfit.ytdActual - totals.netProfit.ytdBudget)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Lock Review Button */}
          {!reviewLocked && reviewedCount === totalSubAccounts && totalSubAccounts > 0 && (
            <div className="flex justify-end">
              <Button 
                onClick={lockReview}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                data-testid="lock-review-btn"
              >
                <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Lock Review
              </Button>
            </div>
          )}
        </>
      )}

      {/* Disagreement Dialog - Enhanced for CFO Workflow */}
      <Dialog 
        open={disagreementDialog.open} 
        onOpenChange={(open) => !open && setDisagreementDialog({ open: false, item: null })}
      >
        <DialogContent className={`rounded-sm max-w-lg ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Submit Disagreement for CFO Review
            </DialogTitle>
            <DialogDescription>
              This disagreement will be sent to the CFO for review and approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Account Info */}
            <div className={`p-3 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Account</p>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {disagreementDialog.item?.account_code} - {disagreementDialog.item?.account_name}
              </p>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{selectedMonth} Budget</p>
                  <p className={`text-sm font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                    {formatCurrency(disagreementDialog.budget)}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{selectedMonth} Actual</p>
                  <p className={`text-sm font-mono ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                    {formatCurrency(disagreementDialog.actual)}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</p>
                  <p className={`text-sm font-mono ${(disagreementDialog.variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(disagreementDialog.variance)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Reason Selection */}
            <div className="space-y-2">
              <Label>Reason for Disagreement *</Label>
              <Select 
                value={disagreementForm.reason} 
                onValueChange={(value) => setDisagreementForm(prev => ({ ...prev, reason: value }))}
              >
                <SelectTrigger className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="disagreement-reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {DISAGREEMENT_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Detailed Comment */}
            <div className="space-y-2">
              <Label>Detailed Explanation *</Label>
              <Textarea 
                value={disagreementForm.comment}
                onChange={(e) => setDisagreementForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Please explain why you disagree with this variance..."
                className={`rounded-sm min-h-[80px] ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                data-testid="disagreement-comment"
              />
            </div>
            
            {/* Suggested Action */}
            <div className="space-y-2">
              <Label>Suggested Action (Optional)</Label>
              <Textarea 
                value={disagreementForm.suggested_action}
                onChange={(e) => setDisagreementForm(prev => ({ ...prev, suggested_action: e.target.value }))}
                placeholder="What action do you suggest to resolve this?"
                className={`rounded-sm min-h-[60px] ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                data-testid="disagreement-suggested-action"
              />
            </div>
            
            {/* Info Box */}
            <div className={`p-3 rounded-sm flex items-start gap-2 ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
              <Send className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className={`text-xs ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                This disagreement will be submitted to the CFO for review. You'll be notified when a decision is made.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setDisagreementDialog({ open: false, item: null })} 
              className="rounded-sm"
              disabled={submittingReviewRequest}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDisagree}
              disabled={submittingReviewRequest || !disagreementForm.reason || !disagreementForm.comment}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
              data-testid="submit-disagreement-btn"
            >
              {submittingReviewRequest ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit to CFO
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialReviewPage;
